import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import type { MasterSwitchMode, PlacedItem } from './types';
import { useDraggableDialog } from './useDraggableDialog';

interface MasterSwitchDialogProps {
  open: boolean;
  item: PlacedItem | null;
  /** Number of currently highlighted (lit) lines. */
  activeCount: number;
  /** Whether the realistic master currently holds the lights off. */
  isOff: boolean;
  /** Number of lines remembered for restore while off. */
  savedCount: number;
  onSetMode: (itemId: string, mode: MasterSwitchMode) => void;
  onOperate: (itemId: string) => void;
  onClose: () => void;
}

type DialogMode = 'control' | 'settings';

export function MasterSwitchDialog({
  open,
  item,
  activeCount,
  isOff,
  savedCount,
  onSetMode,
  onOperate,
  onClose,
}: MasterSwitchDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<DialogMode>('control');
  const { overlayRef, panelRef, panelStyle, dragHandleProps } = useDraggableDialog(open, { initialPlacement: 'right' });

  useEffect(() => {
    if (open) setMode('control');
  }, [item?.id, open]);

  if (!open || !item) return null;

  const masterMode: MasterSwitchMode = item.masterMode ?? 'simple';
  const realistic = masterMode === 'realistic';

  const controlLabel = realistic
    ? (isOff ? t('floor_plan.master_turn_on') : t('floor_plan.master_turn_off'))
    : t('floor_plan.master_clear_all');
  const controlDisabled = realistic ? (isOff && savedCount === 0) : activeCount === 0;
  const stateLabel = realistic
    ? (isOff ? t('floor_plan.master_state_off') : t('floor_plan.master_state_on'))
    : null;

  return (
    <div ref={overlayRef} className="pointer-events-none absolute inset-0 z-20">
      <div
        ref={panelRef}
        style={panelStyle}
        className="pointer-events-auto absolute w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-border bg-surface shadow-xl"
      >
        <div
          className="cursor-grab border-b border-border bg-surface-2 px-4 py-3 active:cursor-grabbing"
          {...dragHandleProps}
        >
          <div className="text-sm font-semibold text-ink">{t('floor_plan.master_title')}</div>
          <div className="mt-1 text-xs leading-relaxed text-ink-4">
            {mode === 'control' ? t('floor_plan.master_control_hint') : t('floor_plan.master_settings_hint')}
          </div>
        </div>

        <div className="p-4">
          {mode === 'control' ? (
            <div className="rounded-md border border-border/60 bg-surface-2 p-3">
              {stateLabel && (
                <div className="mb-2 flex items-center gap-2 text-[11px] text-ink-4">
                  <span
                    className={[
                      'h-2 w-2 rounded-full',
                      isOff ? 'bg-border-2' : 'bg-warning',
                    ].join(' ')}
                  />
                  {stateLabel}
                </div>
              )}
              <Button
                type="button"
                variant="primary"
                className="w-full justify-center"
                disabled={controlDisabled}
                onClick={() => onOperate(item.id)}
              >
                {controlLabel}
              </Button>
              <div className="mt-2 text-[11px] text-ink-4">
                {realistic && isOff
                  ? `${t('floor_plan.master_saved_lines')}: ${savedCount}`
                  : `${t('floor_plan.master_active_lines')}: ${activeCount}`}
              </div>
            </div>
          ) : (
            <div className="space-y-2 rounded-md border border-border/60 bg-surface-2 p-3">
              <div className="mb-1 text-[11px] font-medium uppercase text-ink-4">
                {t('floor_plan.master_mode')}
              </div>
              <ModeOption
                label={t('floor_plan.master_mode_simple')}
                hint={t('floor_plan.master_mode_simple_hint')}
                selected={!realistic}
                onSelect={() => onSetMode(item.id, 'simple')}
              />
              <ModeOption
                label={t('floor_plan.master_mode_realistic')}
                hint={t('floor_plan.master_mode_realistic_hint')}
                selected={realistic}
                onSelect={() => onSetMode(item.id, 'realistic')}
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-border bg-surface-2 px-4 py-3">
          {mode === 'control' ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => setMode('settings')}>
              {t('floor_plan.master_configure')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => setMode('control')}>
              {t('floor_plan.master_control')}
            </Button>
          )}
          <Button type="button" size="sm" onClick={onClose}>
            {t('floor_plan.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModeOption({
  label,
  hint,
  selected,
  onSelect,
}: {
  label: string;
  hint: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'w-full rounded-md border p-2 text-left transition-colors',
        selected
          ? 'border-accent/50 bg-accent/10'
          : 'border-border/60 bg-surface hover:bg-surface-3',
      ].join(' ')}
    >
      <div className="flex items-center gap-2">
        <span
          className={[
            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border',
            selected ? 'border-accent' : 'border-border-2',
          ].join(' ')}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-accent" />}
        </span>
        <span className={['text-xs font-medium', selected ? 'text-accent' : 'text-ink-2'].join(' ')}>
          {label}
        </span>
      </div>
      <div className="mt-1 pl-6 text-[11px] leading-relaxed text-ink-4">{hint}</div>
    </button>
  );
}
